import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Instagram, Facebook } from "lucide-react";
import { LOGO_URL } from "@/lib/api";
import NextShowBanner from "@/components/NextShowBanner";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/gigs", label: "Gigs" },
  { to: "/tip", label: "Tip" },
  { to: "/request", label: "Request" },
  { to: "/book", label: "Book" },
  { to: "/media", label: "Media" },
  { to: "/contact", label: "Contact" },
];

export const Nav = ({ withBanner = false }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${withBanner ? "top-10" : "top-0"} ${
        scrolled ? "bg-black/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img src={LOGO_URL} alt="Retrofit" className="h-10 md:h-12 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-xs tracking-[0.25em] uppercase font-mono transition-colors relative ${
                  isActive ? "text-white" : "text-neutral-400 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <span className="relative">
                  {l.label}
                  {isActive && (
                    <span className="absolute -bottom-2 left-0 right-0 h-[2px] bg-white" />
                  )}
                </span>
              )}
            </NavLink>
          ))}
          <Link
            to="/book"
            data-testid="nav-cta-book"
            className="btn-retro-solid !py-3 !px-5 !text-[10px]"
          >
            Book the Band
          </Link>
        </nav>

        <button
          className="lg:hidden text-white p-2 -mr-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-black border-t border-white/10" data-testid="mobile-menu">
          <div className="px-5 py-6 flex flex-col gap-5">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-2xl font-heading uppercase tracking-tight ${
                    isActive ? "text-white" : "text-neutral-500"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/book" className="btn-retro-solid mt-3 self-start" data-testid="mobile-cta-book">
              Book the Band
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export const Footer = () => (
  <footer className="bg-black border-t border-white/10 mt-32" data-testid="site-footer">
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 grid md:grid-cols-4 gap-10">
      <div className="md:col-span-2">
        <div className="mb-5">
          <img src={LOGO_URL} alt="Retrofit" className="h-20 w-auto object-contain" />
        </div>
        <p className="text-neutral-400 max-w-md leading-relaxed">
          The Southbay's premier cover band. Beach bars, weddings, festivals — wherever there's a crowd, we bring the party.
        </p>
      </div>
      <div>
        <p className="eyebrow mb-4">Navigate</p>
        <ul className="space-y-2 font-mono text-sm">
          {navLinks.map((l) => (
            <li key={l.to}>
              <Link to={l.to} className="text-neutral-300 hover:text-white transition-colors">
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="eyebrow mb-4">Follow</p>
        <div className="flex gap-4 mb-6">
          <a href="https://instagram.com/iloveretrofitmusic" target="_blank" rel="noreferrer"
             className="text-white hover:opacity-60 transition" aria-label="Instagram" data-testid="footer-instagram">
            <Instagram size={22} />
          </a>
          <a href="https://www.facebook.com/retrofitmusic" target="_blank" rel="noreferrer"
             className="text-white hover:opacity-60 transition" aria-label="Facebook" data-testid="footer-facebook">
            <Facebook size={22} />
          </a>
        </div>
        <p className="font-mono text-xs text-neutral-500">
          Venmo ~ @Retrofit-Seth
        </p>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-6 flex flex-col md:flex-row justify-between gap-3 font-mono text-xs text-neutral-500">
        <span>© {new Date().getFullYear()} Retrofit Music. All sound reserved.</span>
        <Link to="/playlist" className="hover:text-white" data-testid="footer-client-playlist">Booked Client? Submit Playlist →</Link>
      </div>
    </div>
  </footer>
);

const Layout = ({ children }) => {
  const location = useLocation();
  const showBanner = location.pathname === "/";
  return (
    <div className="grain min-h-screen bg-neutral-950 text-white">
      {showBanner && <NextShowBanner />}
      <Nav withBanner={showBanner} />
      <main className={showBanner ? "pt-[120px]" : "pt-20"}>{children}</main>
      <Footer />
    </div>
  );
};

export default Layout;
